<script setup lang="ts">
import { computed } from "vue";

import IconButton from "@/components/ui/IconButton/IconButton.vue";
import SvgIcon from "@/components/ui/SvgIcon/SvgIcon.vue";
import type { Icon } from "@/model/icon";
import { useWorkModeStore } from "@/store/workMode";

import MenuItemDelimiter from "./MenuItemDelimiter.vue";

const props = defineProps<{ icon?: Icon; title: string; isModal?: boolean; noClose?: boolean }>();
const emit = defineEmits<{ close: [] }>();

const darkMode = computed(() => !props.isModal && useWorkModeStore().isExecutionMode);
const titleId = computed(() => (props.isModal ? "modal-header-title" : "page-header-title"));
</script>

<template>
  <header class="page-header" :class="{ 'dark-mode': darkMode }">
    <div class="title">
      <SvgIcon v-if="icon" :name="icon" height="20" width="20" />
      <div v-else class="no-icon" />
      <h1 :id="titleId">{{ title }}</h1>
    </div>
    <MenuItemDelimiter v-if="$slots.default" class="delimiter" />
    <div class="content content-left">
      <slot />
    </div>
    <div class="content">
      <slot name="right" />
    </div>
    <template v-if="!noClose">
      <MenuItemDelimiter v-if="$slots.right" class="delimiter" />
      <IconButton icon="system/close" :aria-label="$t('general.close')" @click="emit('close')" />
    </template>
  </header>
</template>

<style scoped lang="scss">
@use "@/styles/variables";
@use "@/styles/variables/colors";
@use "@/styles/z-index";
@use "@/styles/mixins/typography";
@use "@/styles/global/theme-dark";

$bg-transition: 0.3s ease-in-out background-color;

.page-header {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: variables.$top-header-height;
  padding: 4px;
  display: flex;
  align-items: center;
  background-color: colors.$background-ui;
  border-bottom: 1px solid colors.$border-subtle;
  transition: $bg-transition;
  z-index: z-index.$menu;

  @include typography.medium;

  &.dark-mode {
    @include theme-dark.theme-dark;
  }

  :deep(.menu-item-delimiter) {
    margin: 0 6px;
  }

  .title {
    display: flex;
    align-items: center;
    color: colors.$text;

    @include typography.medium("bold");

    svg {
      margin: 12px;
    }

    .no-icon {
      width: 12px;
    }
  }

  .delimiter {
    margin: 0 14px;
  }

  .content {
    display: flex;
    align-items: center;
    gap: 8px;
    color: colors.$text;
  }

  .content-left {
    flex-grow: 1;
  }
}
</style>
